import React, { useState, useEffect } from 'react';
import { Card, Typography, Input, Button, Tooltip } from "@material-tailwind/react";
import axios from 'axios';
import { FaTrash, FaPlus, FaCheck, FaTimes, FaPenAlt, FaUser, FaUserCheck, FaFileInvoice, FaCreditCard, FaRobot, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../AuthContext';
import Alerty from '../../common/Alerty';
const { rootUrl } = require('../../../config/config');

// Custom Tooltip Component
const CustomTooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const handleMouseEnter = (e) => {
        setIsVisible(true);
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
        setMousePosition({ x: e.clientX, y: e.clientY });
    };

    return (
        <div className="relative inline-block">
            <div
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setIsVisible(false)}
                className="cursor-help"
            >
                {children}
            </div>
            {isVisible && (
                <div
                    className="fixed z-[9999] px-3 py-2 text-sm text-white bg-gray-900 rounded-lg shadow-xl text-center whitespace-normal pointer-events-none"
                    style={{
                        zIndex: 9999,
                        backgroundColor: '#1f2937',
                        color: 'white',
                        border: '1px solid #374151',
                        minWidth: '100px',
                        maxWidth: '300px',
                        top: mousePosition.y - 10,
                        left: mousePosition.x + 10,
                        transform: 'translateY(-100%)'
                    }}
                >
                    <div className="font-medium">{content}</div>
                </div>
            )}
        </div>
    );
};

const CarStatus = () => {
    const [statuses, setStatuses] = useState([]);
    const [carPayTypes, setCarPayTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [editCode, setEditCode] = useState('');
    const [editName, setEditName] = useState('');
    const [newCode, setNewCode] = useState('');
    const [newName, setNewName] = useState('');
    const { user } = useAuth();

    const [editActive, setEditActive] = useState(false);
    const [editOperational, setEditOperational] = useState(false);
    const [editDefault, setEditDefault] = useState(false);
    const [editHasDriver, setEditHasDriver] = useState(false);
    const [editDefAssign, setEditDefAssign] = useState(false);
    const [editInvoice, setEditInvoice] = useState(false);
    const [editDefPayType, setEditDefPayType] = useState('');
    const [editAutoPost, setEditAutoPost] = useState(false);
    const [newOperational, setNewOperational] = useState(true);
    const [newDefault, setNewDefault] = useState(false);
    const [newHasDriver, setNewHasDriver] = useState(false);
    const [newDefAssign, setNewDefAssign] = useState(false);
    const [newInvoice, setNewInvoice] = useState(false);
    const [newDefPayType, setNewDefPayType] = useState('');
    const [newAutoPost, setNewAutoPost] = useState(false);

    const fetchCarPayTypes = async () => {
        try {
            const response = await axios.get(`${rootUrl}/payments/car-types`);
            setCarPayTypes(response.data.data || []);
        } catch (err) {
            console.error('Failed to fetch car payment types:', err);
        }
    };

    const createStatus = async (e) => {

        e.preventDefault();
        if (!newCode.trim() || !newName.trim()) {
            setError('Code and name are required.');
            return;
        }
        try {
            await axios.post(`${rootUrl}/cars/status`, {
                code: newCode,
                name: newName,
                active: true,
                operational: newOperational,
                def_status: newDefault,
                has_driver: newHasDriver,
                def_assign: newDefAssign,
                invoice: newInvoice,
                def_pay_type: newDefPayType,
                auto_post: newAutoPost,
                input_by: user.username
            });
            // clear inputs
            setNewCode('');
            setNewName('');
            setNewOperational(false);
            setNewDefault(false);
            setNewHasDriver(false);
            setNewDefAssign(false);
            setNewInvoice(false);
            setNewDefPayType('');
            setNewAutoPost(false);
            setError('');
            fetchStatuses();
        } catch (err) {
            setError('Failed to create new car status.');
        }
    };

    const fetchStatuses = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${rootUrl}/cars/status`);
            setStatuses(response.data.data);
            setError('');
        } catch (err) {
            setError('Failed to fetch car statuses.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatuses();
        fetchCarPayTypes();
    }, []);

    const startEdit = (status) => {
        setEditingId(status.id);
        setEditCode(status.code);
        setEditName(status.name);
        setEditActive(status.active)
        setEditOperational(status.operational)
        setEditDefault(status.def_status)
        setEditHasDriver(status.has_driver)
        setEditDefAssign(status.def_assign)
        setEditInvoice(status.invoice || false)
        setEditDefPayType(status.def_pay_type || '')
        setEditAutoPost(status.auto_post || false)
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditCode('');
        setEditName('');
        setEditActive(false)
        setEditOperational(false)
        setEditDefault(false)
        setEditHasDriver(false)
        setEditDefAssign(false)
        setEditInvoice(false)
        setEditDefPayType('')
        setEditAutoPost(false)
    };

    const saveEdit = async (id) => {
        try {
            await axios.put(`${rootUrl}/cars/status/${id}`, {
                code: editCode,
                name: editName,
                active: editActive,
                operational: editOperational,
                def_status: editDefault,
                has_driver: editHasDriver,
                def_assign: editDefAssign,
                invoice: editInvoice,
                def_pay_type: editDefPayType,
                auto_post: editAutoPost,
                edit_by: user.username
            });
            cancelEdit();
            fetchStatuses();
        } catch (err) {
            setError('Failed to update car status.');
        }
    };

    const deleteStatus = async (id) => {
        if (!window.confirm('Are you sure you want to delete this car status?')) return;
        try {
            await axios.delete(`${rootUrl}/car/status/${id}`);
            fetchStatuses();
        } catch (err) {
            setError('Failed to delete car status.');
        }
    };



    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
            {/* Header Section */}
            <div className="text-center mb-8">
                <h1 className="text-4xl font-bold text-brand-secondary mb-2">Car Status Management</h1>
                <div className="w-24 h-1 bg-brand-primary mx-auto rounded-full"></div>
            </div>

            {error && (
                <div className='mb-6'>
                    <Alerty color='orange' message={error} setOpen={setError} />
                </div>
            )}

            {/* Main Card Container */}
            <Card className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary mb-6">
                    Add New Car Status
                </Typography>

                <form
                    onSubmit={createStatus}
                    className="mb-8 p-6 bg-gray-50 rounded-lg"
                >
                    {/* First Row - Basic Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                        <Input
                            type="text"
                            label="Status Code"
                            value={newCode}
                            onChange={e => setNewCode(e.target.value.slice(0, 3).toUpperCase())}
                            maxLength={3}
                            className="w-full"
                            required
                        />
                        <Input
                            type="text"
                            label="Status Name"
                            value={newName}
                            onChange={e => setNewName(e.target.value.toUpperCase())}
                            className="w-full"
                            required
                        />
                    </div>

                    {/* Second Row - Options */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                        {/* Operational */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <label className="flex items-center justify-center text-gray-700 text-sm font-medium cursor-pointer">
                                <div className="relative inline-block w-12 h-6 mr-2">
                                    <input
                                        type="checkbox"
                                        className="absolute opacity-0 w-full h-full peer cursor-pointer"
                                        checked={newOperational}
                                        onChange={e => setNewOperational(e.target.checked)}
                                    />
                                    <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-brand-primary transition-colors duration-300"></div>
                                    <div className="absolute top-0 left-0 w-6 h-6 bg-white border-2 border-gray-400 rounded-full transition-transform duration-300 peer-checked:translate-x-full peer-checked:border-brand-primary"></div>
                                </div>
                            </label>
                            <span className="text-xs text-gray-600 text-center font-medium">Operational</span>
                        </div>

                        {/* Has Driver */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <label className="flex items-center justify-center text-gray-700 text-sm font-medium cursor-pointer">
                                <div className="relative inline-block w-12 h-6 mr-2">
                                    <input
                                        type="checkbox"
                                        className="absolute opacity-0 w-full h-full peer cursor-pointer"
                                        checked={newHasDriver}
                                        onChange={e => setNewHasDriver(e.target.checked)}
                                    />
                                    <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-brand-primary transition-colors duration-300"></div>
                                    <div className="absolute top-0 left-0 w-6 h-6 bg-white border-2 border-gray-400 rounded-full transition-transform duration-300 peer-checked:translate-x-full peer-checked:border-brand-primary"></div>
                                </div>
                            </label>
                            <span className="text-xs text-gray-600 text-center font-medium">Has Driver</span>
                        </div>

                        {/* Default Assign */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <label className="flex items-center justify-center text-gray-700 text-sm font-medium cursor-pointer">
                                <div className="relative inline-block w-12 h-6 mr-2">
                                    <input
                                        type="checkbox"
                                        className="absolute opacity-0 w-full h-full peer cursor-pointer"
                                        checked={newDefAssign}
                                        onChange={e => setNewDefAssign(e.target.checked)}
                                    />
                                    <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-brand-primary transition-colors duration-300"></div>
                                    <div className="absolute top-0 left-0 w-6 h-6 bg-white border-2 border-gray-400 rounded-full transition-transform duration-300 peer-checked:translate-x-full peer-checked:border-brand-primary"></div>
                                </div>
                            </label>
                            <span className="text-xs text-gray-600 text-center font-medium">Default Assign</span>
                        </div>

                        {/* Invoice Default */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <label className="flex items-center justify-center text-gray-700 text-sm font-medium cursor-pointer">
                                <div className="relative inline-block w-12 h-6 mr-2">
                                    <input
                                        type="checkbox"
                                        className="absolute opacity-0 w-full h-full peer cursor-pointer"
                                        checked={newInvoice}
                                        onChange={e => setNewInvoice(e.target.checked)}
                                    />
                                    <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-brand-primary transition-colors duration-300"></div>
                                    <div className="absolute top-0 left-0 w-6 h-6 bg-white border-2 border-gray-400 rounded-full transition-transform duration-300 peer-checked:translate-x-full peer-checked:border-brand-primary"></div>
                                </div>
                            </label>
                            <span className="text-xs text-gray-600 text-center font-medium">Invoice Default</span>
                        </div>

                        {/* Auto Post */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <label className="flex items-center justify-center text-gray-700 text-sm font-medium cursor-pointer">
                                <div className="relative inline-block w-12 h-6 mr-2">
                                    <input
                                        type="checkbox"
                                        className="absolute opacity-0 w-full h-full peer cursor-pointer"
                                        checked={newAutoPost}
                                        onChange={e => setNewAutoPost(e.target.checked)}
                                    />
                                    <div className="w-full h-full bg-gray-300 rounded-full peer-checked:bg-brand-primary transition-colors duration-300"></div>
                                    <div className="absolute top-0 left-0 w-6 h-6 bg-white border-2 border-gray-400 rounded-full transition-transform duration-300 peer-checked:translate-x-full peer-checked:border-brand-primary"></div>
                                </div>
                            </label>
                            <span className="text-xs text-gray-600 text-center font-medium">Auto Post</span>
                        </div>

                        {/* Default Pay Type */}
                        <div className="flex flex-col items-center space-y-2 p-3 bg-white rounded-lg border border-gray-200">
                            <div className="w-full">
                                <select
                                    value={newDefPayType}
                                    onChange={(e) => setNewDefPayType(e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                                >
                                    <option value="">None</option>
                                    {carPayTypes.map((payType) => (
                                        <option key={payType.type} value={payType.type}>
                                            {payType.short_desc || payType.type}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <span className="text-xs text-gray-600 text-center font-medium">Default Pay Type</span>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="mt-6 flex justify-center">
                        <Button
                            type="submit"
                            variant="filled"
                            color="blue"
                            className="px-8 py-3 flex items-center justify-center gap-2 text-base font-semibold"
                        >
                            <FaPlus /> Add New Status
                        </Button>
                    </div>
                </form>

                {/* Table Section */}
                <Typography variant="h6" color="blue-gray" className="font-semibold text-brand-secondary mb-4">
                    Car Status List
                </Typography>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
                            <Typography>Loading statuses...</Typography>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Card className="h-full w-full border border-gray-100">
                            <table className="w-full table-auto text-left min-w-[800px]">
                                <thead>
                                    <tr>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                ID
                                            </Typography>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                Code
                                            </Typography>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                Name
                                            </Typography>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                Active
                                            </Typography>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Op
                                                </Typography>
                                                <CustomTooltip content="Operational - Can be used for business">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Def
                                                </Typography>
                                                <CustomTooltip content="Default Status - Automatically assigned to new cars">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Driver
                                                </Typography>
                                                <CustomTooltip content="Has Driver - Driver is assigned">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Assign
                                                </Typography>
                                                <CustomTooltip content="Default Assign - Used when assigning drivers">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Invoice
                                                </Typography>
                                                <CustomTooltip content="Invoice Default - Cars get automatically invoiced weekly">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Pay Type
                                                </Typography>
                                                <CustomTooltip content="Default Payment Type used when auto posting">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                    Auto
                                                </Typography>
                                                <CustomTooltip content="Auto Post - Financial transactions posted automatically">
                                                    <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help text-xs" />
                                                </CustomTooltip>
                                            </div>
                                        </th>
                                        <th className="border-b border-gray-200 bg-gray-50 p-2 text-center">
                                            <Typography variant="small" color="blue-gray" className="font-semibold leading-none text-brand-secondary text-xs">
                                                Actions
                                            </Typography>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {statuses && Array.isArray(statuses) && statuses.map((status) => (
                                        <tr key={status.id} className="even:bg-gray-50/50 hover:bg-brand-misty/30 transition-colors duration-200">
                                            <td className="p-2">
                                                <Typography variant="small" color="blue-gray" className="font-normal text-brand-secondary text-xs">
                                                    {status.id}
                                                </Typography>
                                            </td>
                                            <td className="p-2">
                                                {editingId === status.id ? (
                                                    <Input
                                                        type="text"
                                                        size="sm"
                                                        value={editCode}
                                                        onChange={(e) => setEditCode(e.target.value)}
                                                        className="w-16"
                                                    />
                                                ) : (
                                                    <Typography variant="small" color="blue-gray" className="font-normal text-brand-secondary text-xs">
                                                        {status.code}
                                                    </Typography>
                                                )}
                                            </td>
                                            <td className="p-2">
                                                {editingId === status.id ? (
                                                    <Input
                                                        type="text"
                                                        size="sm"
                                                        value={editName}
                                                        onChange={(e) => setEditName(e.target.value)}
                                                        className="w-32"
                                                    />
                                                ) : (
                                                    <Typography variant="small" color="blue-gray" className="font-normal text-brand-secondary text-xs">
                                                        {status.name}
                                                    </Typography>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editActive}
                                                            onChange={(e) => setEditActive(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.active ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaCheck className="text-green-700" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editOperational}
                                                            onChange={(e) => setEditOperational(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.operational ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaCheck className="text-green-700" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editDefault}
                                                            onChange={(e) => setEditDefault(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.def_status ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaCheck className="text-green-700" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editHasDriver}
                                                            onChange={(e) => setEditHasDriver(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.has_driver ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaUser className="text-blue-600" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editDefAssign}
                                                            onChange={(e) => setEditDefAssign(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.def_assign ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaUserCheck className="text-green-600" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editInvoice}
                                                            onChange={(e) => setEditInvoice(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.invoice ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaFileInvoice className="text-green-600" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <select
                                                        value={editDefPayType}
                                                        onChange={(e) => setEditDefPayType(e.target.value)}
                                                        className="w-24 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-brand-primary"
                                                    >
                                                        <option value="">None</option>
                                                        {carPayTypes.map((payType) => (
                                                            <option key={payType.type} value={payType.type}>
                                                                {payType.short_desc || payType.type}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : status.def_pay_type ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaCreditCard className="text-blue-600" title={status.def_pay_type} />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <label className="flex cursor-pointer justify-center items-center">
                                                        <input
                                                            type="checkbox"
                                                            className="sr-only peer"
                                                            checked={editAutoPost}
                                                            onChange={(e) => setEditAutoPost(e.target.checked)}
                                                        />
                                                        <div
                                                            className="w-11 h-6 bg-gray-200 rounded-full transition-colors peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-primary/30 peer-checked:bg-brand-primary peer-checked:after:translate-x-full relative after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-transform"
                                                        />
                                                    </label>
                                                ) : status.auto_post ? (
                                                    <div className="flex justify-center items-center">
                                                        <FaRobot className="text-green-600" />
                                                    </div>
                                                ) : (
                                                    <div className="flex justify-center items-center">
                                                        <FaTimes className="text-red-700" />
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-center">
                                                {editingId === status.id ? (
                                                    <div className="flex space-x-2 justify-center">
                                                        <button
                                                            onClick={() => saveEdit(status.id)}
                                                            className="bg-transparent text-green-600 p-2 rounded-lg hover:bg-green-50 transition-colors duration-200"
                                                            title="Save"
                                                        >
                                                            <FaCheck />
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            className="bg-transparent text-orange-600 p-2 rounded-lg hover:bg-orange-50 transition-colors duration-200"
                                                            title="Cancel"
                                                        >
                                                            <FaTimes />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="flex space-x-2 justify-center">
                                                        <Tooltip content="Edit" placement="top">
                                                            <Button
                                                                size="sm"
                                                                variant="outlined"
                                                                className="p-2 w-8 h-8 flex items-center justify-center border-gray-300 text-gray-600 hover:bg-gray-50 rounded-full"
                                                                onClick={() => startEdit(status)}
                                                                aria-label={`Edit status ${status.code}`}
                                                            >
                                                                <FaPenAlt />
                                                            </Button>
                                                        </Tooltip>

                                                        <Tooltip content="Delete" placement="top">
                                                            <Button
                                                                size="sm"
                                                                className="p-2 w-8 h-8 flex items-center justify-center bg-red-700 hover:bg-red-800 text-white rounded-full"
                                                                onClick={() => deleteStatus(status.id)}
                                                                aria-label={`Delete status ${status.code}`}
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        </Tooltip>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </Card>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default CarStatus;
